import { ogImageMeta } from "@/lib/seo/og-image";
import { createFileRoute, notFound, useLoaderData } from "@tanstack/react-router";
import { LocaleLink } from "@/components/site/LocaleLink";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PostBody, extractToc } from "@/components/insights/PostBody";
import { TableOfContents } from "@/components/insights/TableOfContents";
import { RelatedServices } from "@/components/insights/RelatedServices";
import { GatedDownload } from "@/components/insights/GatedDownload";
import { PostByline } from "@/components/insights/PostByline";
import { getPost } from "@/lib/wp.functions";
import type { WpPost } from "@/server/wp.server";
import { useT } from "@/lib/i18n/I18nProvider";
import { urlFor } from "@/lib/seo/schema";
import { blogPostSchemas } from "@/lib/seo/page-schema";
import {
  blogMetaDescription,
  blogMetaTitle,
  canonicalSlugFor,
  stripHtml,
} from "@/lib/seo/blog-meta";

import { ArticlePage } from "@/components/insights/ArticlePage";
export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const { post } = await getPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return { meta: [{ title: "Article - Perceptica" }] };
    }
    const title = stripHtml(post.title.rendered);
    const metaTitle = blogMetaTitle(post, title);
    const desc = blogMetaDescription(post);
    const image = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
    const shareImage = image?.startsWith("/") ? `https://perceptica.com${image}` : image;
    const author = post._embedded?.author?.[0]?.name;
    const canonicalSlug = canonicalSlugFor(post);
    const isDuplicate = canonicalSlug !== post.slug;
    const canonical = urlFor("en", `/blog/${canonicalSlug}`);
    return {
      meta: [
        { title: metaTitle },
        { name: "description", content: desc },
        ...(isDuplicate || post.seo?.noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        ...ogImageMeta("en", "/blog", title).map((m) =>
          shareImage && (("property" in m && m.property === "og:image") || ("name" in m && m.name === "twitter:image"))
            ? { ...m, content: shareImage }
            : m,
        ),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ],
    links: [
    ],
      scripts: blogPostSchemas({
        locale: "en",
        canonicalSlug,
        title,
        description: desc,
        image: shareImage,
        datePublished: post.date,
        dateModified: post.modified,
        authorName: author,
        contentHtml: post.content?.rendered,
      }),
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-24 text-center lg:px-10">
        <h1 className="font-display text-3xl font-medium text-foreground">Article not found</h1>
        <p className="mt-4 text-foreground/70">It may have been moved or unpublished.</p>
        <LocaleLink to="/blog" className="mt-6 inline-block text-accent underline underline-offset-2">
          Back to Blog
        </LocaleLink>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-24 text-center lg:px-10">
        <h1 className="font-display text-2xl font-medium text-foreground">Couldn't load this article</h1>
        <p className="mt-3 text-foreground/70">An error occurred loading this article. Please try again.</p>
        {import.meta.env.DEV && (
          <p className="mt-2 text-[12px] text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        )}
        <LocaleLink to="/blog" className="mt-6 inline-block text-accent underline underline-offset-2">
          Back to Blog
        </LocaleLink>
      </main>
      <SiteFooter />
    </div>
  ),
  component: ArticlePage,
});
